/**
 * Attach `remote.v1.getAuthHeaders` so session API calls send Firebase ID tokens (required by `lecturaRemoteApi` for POST create / command / emote / PATCH).
 */
import type { AuthService, LecturaConfig } from '../../../core/types';

function hasFirebaseConfig(config: LecturaConfig): boolean {
  return !!(
    config.firebaseConfig ||
    (config.chat && typeof config.chat === 'object' && (config.chat as { firebaseConfig?: unknown }).firebaseConfig)
  );
}

/** Reads `window.firebase.auth().currentUser` when scripts are already loaded (e.g. after `ensureAuth`). */
function createWindowFirebaseAuthHeaders(): () => Promise<Record<string, string>> {
  return async () => {
    try {
      const fb = (typeof window !== 'undefined' && (window as { firebase?: { auth?: () => { currentUser: { getIdToken: (force?: boolean) => Promise<string> } | null } } }).firebase) || undefined;
      if (!fb?.auth) return {};
      const u = fb.auth().currentUser;
      if (!u) return {};
      const token = await u.getIdToken();
      return token ? { Authorization: `Bearer ${token}` } : {};
    } catch {
      return {};
    }
  };
}

/**
 * When `remote.v1.getAuthHeaders` is not set, supply one from Firebase:
 * - Prefer `authService.getIdToken` (presentation / orchestrator).
 * - Else, if Firebase config exists, use `window.firebase` (remote entry before full auth wiring).
 */
export function mergeRemoteV1SessionAuth(config: LecturaConfig, authService: AuthService | null): LecturaConfig {
  const v1 = config.remote?.v1;
  if (!v1 || v1.enabled === false || v1.devMock) return config;
  if (typeof v1.getAuthHeaders === 'function') return config;

  const fromService =
    authService && typeof authService.getIdToken === 'function'
      ? async (): Promise<Record<string, string>> => {
          const token = await authService.getIdToken!();
          return token ? { Authorization: `Bearer ${token}` } : {};
        }
      : null;

  const fromWindow = hasFirebaseConfig(config) ? createWindowFirebaseAuthHeaders() : null;
  const getAuthHeaders = fromService ?? fromWindow;
  if (!getAuthHeaders) return config;

  return {
    ...config,
    remote: {
      ...config.remote,
      v1: {
        ...v1,
        getAuthHeaders
      }
    }
  };
}
